import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { offlineStorageService } from '@/services/offlineStorageService';
import { useToast } from '@/hooks/use-toast';

interface OpsManager {
  id: string;
  email: string;
  full_name?: string;
  company_id: string;
  company_name: string;
}

interface OpsManagerAuthContextType {
  opsManager: OpsManager | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

const OpsManagerAuthContext = createContext<OpsManagerAuthContextType | undefined>(undefined);

export function OpsManagerAuthProvider({ children }: { children: React.ReactNode }) {
  const [opsManager, setOpsManager] = useState<OpsManager | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  async function checkAuthStatus() {
    try {
      // Check stored auth
      const storedAuth = await offlineStorageService.getOpsManagerAuth();
      
      if (storedAuth) {
        // Verify session with Supabase
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          // Fetch user companies and verify role
          const { data: userCompanies } = await supabase
            .from('user_companies')
            .select(`
              role,
              companies (
                id,
                name
              )
            `)
            .eq('user_id', session.user.id)
            .eq('role', 'fleet_ops_manager');

          if (userCompanies && userCompanies.length > 0) {
            const firstCompany = userCompanies[0];
            setOpsManager({
              id: session.user.id,
              email: session.user.email!,
              full_name: session.user.user_metadata?.full_name,
              company_id: firstCompany.companies.id,
              company_name: firstCompany.companies.name
            });
          } else {
            // No valid role, clear auth
            await offlineStorageService.clearOpsManagerAuth();
          }
        } else {
          await offlineStorageService.clearOpsManagerAuth();
        }
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      await offlineStorageService.clearOpsManagerAuth();
    } finally {
      setIsLoading(false);
    }
  }

  async function login(email: string, password: string): Promise<boolean> {
    try {
      // Authenticate with Supabase
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (authError) {
        toast({
          title: 'Erro ao fazer login',
          description: authError.message === 'Invalid login credentials'
            ? 'Email ou senha incorretos'
            : authError.message,
          variant: 'destructive'
        });
        return false;
      }

      if (!authData.user) {
        toast({
          title: 'Erro ao fazer login',
          description: 'Não foi possível autenticar',
          variant: 'destructive'
        });
        return false;
      }

      // Check if user has fleet_ops_manager role
      const { data: userCompanies, error: companiesError } = await supabase
        .from('user_companies')
        .select(`
          role,
          companies (
            id,
            name
          )
        `)
        .eq('user_id', authData.user.id)
        .eq('role', 'fleet_ops_manager');

      if (companiesError) {
        console.error('Error fetching user companies:', companiesError);
        toast({
          title: 'Erro',
          description: 'Erro ao verificar permissões',
          variant: 'destructive'
        });
        await supabase.auth.signOut();
        return false;
      }

      if (!userCompanies || userCompanies.length === 0) {
        toast({
          title: 'Acesso Negado',
          description: 'Você não tem permissão de Gestor de Operações',
          variant: 'destructive'
        });
        await supabase.auth.signOut();
        return false;
      }

      // Save auth data
      const firstCompany = userCompanies[0];
      await offlineStorageService.saveOpsManagerAuth(
        authData.user.id,
        authData.session.access_token,
        firstCompany.companies.id
      );

      setOpsManager({
        id: authData.user.id,
        email: authData.user.email!,
        full_name: authData.user.user_metadata?.full_name,
        company_id: firstCompany.companies.id,
        company_name: firstCompany.companies.name
      });

      toast({
        title: 'Login realizado',
        description: `Bem-vindo, ${authData.user.user_metadata?.full_name || authData.user.email}!`
      });

      return true;
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: 'Erro',
        description: 'Erro inesperado ao fazer login',
        variant: 'destructive'
      });
      return false;
    }
  }

  async function logout() {
    try {
      await supabase.auth.signOut();
      await offlineStorageService.clearOpsManagerAuth();
      setOpsManager(null);
      
      toast({
        title: 'Logout realizado',
        description: 'Até logo!'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  return (
    <OpsManagerAuthContext.Provider
      value={{
        opsManager,
        isLoading,
        isAuthenticated: !!opsManager,
        login,
        logout
      }}
    >
      {children}
    </OpsManagerAuthContext.Provider>
  );
}

export function useOpsManagerAuth() {
  const context = useContext(OpsManagerAuthContext);
  if (context === undefined) {
    throw new Error('useOpsManagerAuth must be used within OpsManagerAuthProvider');
  }
  return context;
}
